import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, Tooltip } from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';

const canUseFullscreen = () => typeof document !== 'undefined' && document.documentElement && document.fullscreenEnabled;

const PresentationMode = ({ children, header = 'Presentation Mode' }) => {
  const [enabled, setEnabled] = useState(false);
  const fullscreenSupported = useMemo(() => canUseFullscreen(), []);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const handleChange = () => {
      // Keep internal state in sync if the user hits ESC.
      const isFull = Boolean(document.fullscreenElement);
      if (!isFull && enabled) {
        setEnabled(false);
      }
    };
    document.addEventListener('fullscreenchange', handleChange);
    return () => document.removeEventListener('fullscreenchange', handleChange);
  }, [enabled]);

  const enterFullscreen = async () => {
    if (!fullscreenSupported) return;
    try {
      await document.documentElement.requestFullscreen();
    } catch (_err) {
      // noop (Fullscreen can be blocked by browser policy)
    }
  };

  const exitFullscreen = async () => {
    if (!fullscreenSupported) return;
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      }
    } catch (_err) {
      // noop
    }
  };

  const toggle = async () => {
    const next = !enabled;
    setEnabled(next);
    if (next) await enterFullscreen();
    else await exitFullscreen();
  };

  return (
    <Box
      data-testid="presentation-mode-root"
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        border: (theme) => `1px solid ${theme.palette.divider}`,
      }}
    >
      <Box
        sx={{
          p: 1.5,
          display: 'flex',
          gap: 1,
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: 'background.paper',
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip size="small" label={header} color={enabled ? 'success' : 'default'} variant="outlined" />
          {!fullscreenSupported && (
            <Tooltip title="Fullscreen API not available in this browser context">
              <Chip size="small" label="Fullscreen unsupported" variant="outlined" />
            </Tooltip>
          )}
        </Box>
        <Button
          size="small"
          variant={enabled ? 'contained' : 'outlined'}
          startIcon={enabled ? <FullscreenExitIcon /> : <FullscreenIcon />}
          onClick={() => void toggle()}
          aria-label="Toggle presentation mode"
          data-testid="presentation-mode-toggle"
        >
          {enabled ? 'Exit' : 'Present'}
        </Button>
      </Box>
      <Box
        data-testid="presentation-mode-content"
        sx={{
          p: { xs: 1.5, md: 2.5 },
          backgroundColor: enabled ? 'background.default' : 'background.paper',
          fontSize: enabled ? 18 : undefined,
          lineHeight: enabled ? 1.6 : undefined,
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default PresentationMode;



